import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type SearchableSelectOption = {
  value: string;
  label: string;
  description?: string;
  group?: string;
};

type SearchableSelectProps = {
  value: string;
  onValueChange: (v: string) => void;
  options: SearchableSelectOption[];
  placeholder: string;
  /** Tekst als er (na filter) geen rijen zijn */
  emptyMessage?: string;
  id?: string;
  "aria-label"?: string;
  disabled?: boolean;
  className?: string;
};

function normalize(s: string) {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function matchesQuery(opt: SearchableSelectOption, q: string) {
  if (!q.trim()) return true;
  const nq = normalize(q);
  const blob = [opt.label, opt.description ?? "", opt.group ?? ""].join(" ");
  return normalize(blob).includes(nq);
}

/**
 * Combobox: typen filtert de lijst. Gebruikt voor lange lijsten medewerkers op de groep-pagina.
 */
export default function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder,
  emptyMessage = "Geen resultaten",
  id: idProp,
  "aria-label": ariaLabel,
  disabled,
  className = "",
}: SearchableSelectProps) {
  const genId = useId();
  const id = idProp ?? genId;
  const listboxId = `${id}-listbox`;
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  /** Voor portal: vaste positie t.o.v. viewport (voorkomt afsnijding door overflow op tabellen) */
  const [listPos, setListPos] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);

  const selected = useMemo(
    () => (value ? options.find((o) => o.value === value) : undefined),
    [value, options]
  );

  const filtered = useMemo(
    () => (open ? options.filter((o) => matchesQuery(o, query)) : []),
    [open, options, query]
  );

  // Indexen in filtered voor toetsenbord
  const flatRows = useMemo(() => {
    if (!open || filtered.length === 0) return [] as { type: "header" | "item"; key: string; label?: string; option?: SearchableSelectOption; idx: number }[];
    const out: { type: "header" | "item"; key: string; label?: string; option?: SearchableSelectOption; idx: number }[] = [];
    let i = 0;
    let lastG: string | undefined;
    for (const o of filtered) {
      if (o.group !== lastG) {
        lastG = o.group;
        if (o.group) {
          out.push({ type: "header", key: `h-${o.group}-${i}`, label: o.group, idx: -1 });
        }
      }
      out.push({ type: "item", key: `i-${o.value}`, option: o, idx: i });
      i += 1;
    }
    return out;
  }, [open, filtered]);

  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (open) setActiveIdx(0);
  }, [open, query, filtered.length]);

  useEffect(() => {
    return () => {
      if (blurTimer.current) clearTimeout(blurTimer.current);
    };
  }, []);

  function updateListPosition() {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 8;
    const below = r.bottom + 4;
    const spaceBelow = window.innerHeight - below - margin;
    const spaceAbove = r.top - margin;
    const wantMax = 320;
    const openUp = spaceBelow < 120 && spaceAbove > spaceBelow;
    const maxH = Math.min(
      wantMax,
      Math.max(120, openUp ? spaceAbove - 4 : spaceBelow)
    );
    const top = openUp ? r.top - 4 - maxH : below;
    setListPos({ top, left: r.left, width: r.width, maxHeight: maxH });
  }

  useLayoutEffect(() => {
    if (!open) {
      setListPos(null);
      return;
    }
    updateListPosition();
  }, [open, filtered.length]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updateListPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open]);

  // Sluiten bij klik buiten
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (listRef.current?.contains(t)) return;
      setOpen(false);
      setQuery("");
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const showInput = open;
  const displayClosed = !open && selected;
  const inputValue = showInput ? query : displayClosed ? selected!.label : "";

  function openDropdown() {
    if (disabled) return;
    setQuery("");
    setOpen(true);
  }

  function pickOption(opt: SearchableSelectOption) {
    onValueChange(opt.value);
    setQuery("");
    setOpen(false);
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        openDropdown();
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setQuery("");
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((a) => Math.min(a + 1, Math.max(0, filtered.length - 1)));
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((a) => Math.max(0, a - 1));
    }
    if (e.key === "Enter" && filtered.length > 0) {
      e.preventDefault();
      const opt = filtered[activeIdx] ?? filtered[0];
      if (opt) pickOption(opt);
    }
  };

  const listContent = listPos && open && !disabled && (
    <ul
      ref={listRef}
      id={listboxId}
      className="searchable-select__list searchable-select__list--fixed"
      role="listbox"
      style={{
        position: "fixed",
        zIndex: 10_000,
        top: listPos.top,
        left: listPos.left,
        width: listPos.width,
        maxHeight: listPos.maxHeight,
      }}
    >
      {flatRows.length === 0 && (
        <li className="searchable-select__empty" role="presentation">
          {emptyMessage}
        </li>
      )}
      {flatRows.map((row) => {
        if (row.type === "header" && row.label) {
          return (
            <li key={row.key} className="searchable-select__group" role="presentation">
              {row.label}
            </li>
          );
        }
        const o = row.option;
        if (!o) return null;
        const isActive = o === filtered[activeIdx];
        return (
          <li
            key={row.key}
            role="option"
            aria-selected={isActive}
            className={`searchable-select__option${isActive ? " searchable-select__option--active" : ""}`}
            onMouseDown={(e) => {
              e.preventDefault();
              pickOption(o);
            }}
            onMouseEnter={() => setActiveIdx(filtered.indexOf(o))}
          >
            <span className="searchable-select__line1">{o.label}</span>
            {o.description && <span className="searchable-select__line2">{o.description}</span>}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div ref={rootRef} className={`searchable-select ${className}`.trim()} onKeyDown={onKeyDown}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        className="searchable-select__input"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        disabled={disabled}
        placeholder={open ? "Typ om te zoeken…" : placeholder}
        value={inputValue}
        onChange={(e) => {
          const t = e.target.value;
          if (!open) setOpen(true);
          setQuery(t);
        }}
        onFocus={() => {
          if (disabled) return;
          if (blurTimer.current) {
            clearTimeout(blurTimer.current);
            blurTimer.current = null;
          }
          openDropdown();
        }}
        onBlur={() => {
          blurTimer.current = setTimeout(() => {
            setOpen(false);
            setQuery("");
          }, 200);
        }}
      />
      {typeof document !== "undefined" && listContent && createPortal(listContent, document.body)}
    </div>
  );
}
